import type { Trade, KPIs, DailyStats, AssetStats } from '@/lib/types'

export class CalculationsService {
  /**
   * Calcula todos os KPIs principais
   */
  static calculateKPIs(trades: Trade[], initialBalance: number): KPIs {
    // Filtrar apenas operações reais (não demo) e completadas
    const completedTrades = trades.filter(
      (t) => t.status !== 'open' && t.status !== 'pending' && !t.demo
    )

    const winningTrades = completedTrades.filter((t) => t.profit > 0)
    const losingTrades = completedTrades.filter((t) => t.profit < 0)

    const totalProfit = winningTrades.reduce((sum, t) => sum + t.profit, 0)
    const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + t.profit, 0))

    const profitFactor = totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? 999 : 0

    const winRate =
      completedTrades.length > 0 ? (winningTrades.length / completedTrades.length) * 100 : 0

    const avgWin = winningTrades.length > 0 ? totalProfit / winningTrades.length : 0
    const avgLoss = losingTrades.length > 0 ? totalLoss / losingTrades.length : 0

    const netProfit = totalProfit - totalLoss
    const currentBalance = initialBalance + netProfit

    const roi = initialBalance > 0 ? (netProfit / initialBalance) * 100 : 0

    const bestTrade = completedTrades.reduce((max, t) => Math.max(max, t.profit), 0)
    const worstTrade = completedTrades.reduce((min, t) => Math.min(min, t.profit), 0)

    const { currentStreak, maxWinStreak, maxLossStreak } = this.calculateStreaks(completedTrades)

    return {
      current_balance: currentBalance,
      initial_balance: initialBalance,
      total_profit: totalProfit,
      total_loss: totalLoss,
      profit_factor: profitFactor,
      win_rate: winRate,
      total_trades: completedTrades.length,
      winning_trades: winningTrades.length,
      losing_trades: losingTrades.length,
      avg_win: avgWin,
      avg_loss: avgLoss,
      roi,
      best_trade: bestTrade,
      worst_trade: worstTrade,
      current_streak: currentStreak,
      max_win_streak: maxWinStreak,
      max_loss_streak: maxLossStreak,
    }
  }

  /**
   * Calcula sequências (streaks) de vitórias/derrotas
   */
  private static calculateStreaks(trades: Trade[]) {
    let currentStreak = 0
    let maxWinStreak = 0
    let maxLossStreak = 0
    let tempWinStreak = 0
    let tempLossStreak = 0

    for (const trade of trades) {
      if (trade.profit > 0) {
        tempWinStreak++
        tempLossStreak = 0
        currentStreak = tempWinStreak
        maxWinStreak = Math.max(maxWinStreak, tempWinStreak)
      } else if (trade.profit < 0) {
        tempLossStreak++
        tempWinStreak = 0
        currentStreak = -tempLossStreak
        maxLossStreak = Math.max(maxLossStreak, tempLossStreak)
      }
    }

    return { currentStreak, maxWinStreak, maxLossStreak }
  }

  /**
   * Agrupa operações por dia
   */
  static groupByDay(trades: Trade[]): DailyStats[] {
    const grouped = new Map<string, Trade[]>()

    // Filtrar apenas operações reais (não demo)
    const realTrades = trades.filter((t) => !t.demo)

    realTrades.forEach((trade) => {
      const date = trade.created_at.split('T')[0] // YYYY-MM-DD
      if (!grouped.has(date)) {
        grouped.set(date, [])
      }
      grouped.get(date)!.push(trade)
    })

    return Array.from(grouped.entries())
      .map(([date, dayTrades]) => {
        const completedTrades = dayTrades.filter((t) => t.status !== 'open' && t.status !== 'pending')
        const wins = completedTrades.filter((t) => t.profit > 0).length
        const profit = completedTrades.reduce((sum, t) => sum + t.profit, 0)
        const winRate = completedTrades.length > 0 ? (wins / completedTrades.length) * 100 : 0

        return {
          date,
          trades: completedTrades.length,
          profit,
          win_rate: winRate,
        }
      })
      .sort((a, b) => a.date.localeCompare(b.date))
  }

  /**
   * Calcula estatísticas por ativo
   */
  static calculateAssetStats(trades: Trade[]): AssetStats[] {
    const grouped = new Map<string, Trade[]>()

    // Filtrar apenas operações reais (não demo) e completadas
    trades
      .filter((t) => t.status !== 'open' && t.status !== 'pending' && !t.demo)
      .forEach((trade) => {
        const symbol = trade.asset
        if (!grouped.has(symbol)) {
          grouped.set(symbol, [])
        }
        grouped.get(symbol)!.push(trade)
      })

    return Array.from(grouped.entries())
      .map(([symbol, assetTrades]) => {
        const wins = assetTrades.filter((t) => t.profit > 0).length
        const losses = assetTrades.filter((t) => t.profit < 0).length
        const totalProfit = assetTrades.reduce((sum, t) => sum + t.profit, 0)
        const avgProfit = totalProfit / assetTrades.length
        const winRate = (wins / assetTrades.length) * 100

        return {
          symbol,
          name: symbol,
          total_trades: assetTrades.length,
          wins,
          losses,
          win_rate: winRate,
          total_profit: totalProfit,
          avg_profit: avgProfit,
        }
      })
      .sort((a, b) => b.total_trades - a.total_trades)
  }

  /**
   * Calcula curva de equity (evolução do saldo)
   */
  static calculateEquityCurve(
    trades: Trade[],
    currentBalance: number,
  ): Array<{ date: string; balance: number }> {
    // Filtrar apenas operações reais (não demo) e completadas
    const sorted = [...trades]
      .filter((t) => t.status !== 'open' && t.status !== 'pending' && !t.demo)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

    // Se não há trades, retorna apenas saldo atual
    if (sorted.length === 0) {
      return [{ date: new Date().toISOString().split('T')[0], balance: currentBalance }]
    }

    // Calcular saldo inicial retroativamente
    const totalProfit = sorted.reduce((sum, t) => sum + t.profit, 0)
    const initialBalance = currentBalance - totalProfit

    const curve: Array<{ date: string; balance: number }> = [
      { date: sorted[0].created_at.split('T')[0], balance: initialBalance },
    ]

    let balance = initialBalance

    sorted.forEach((trade) => {
      balance += trade.profit
      curve.push({
        date: trade.created_at.split('T')[0],
        balance: balance,
      })
    })

    return curve
  }

  /**
   * Formata valor em reais
   */
  static formatBRL(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  /**
   * Formata porcentagem
   */
  static formatPercent(value: number): string {
    return `${value.toFixed(2)}%`
  }
}
